<template>
  <div class="pdfViewer">
    <div class="pdfToolbar">
      <button class="zoomBtn" :disabled="zoom <= minZoom" @click="setZoom(zoom - step)">−</button>
      <button class="zoomValue" @click="setZoom(1)">{{ Math.round(zoom * 100) }}%</button>
      <button class="zoomBtn" :disabled="zoom >= maxZoom" @click="setZoom(zoom + step)">+</button>
      <span v-if="ratios.length" class="pageCount">{{ ratios.length }} {{ $t("managedInstructionAssignment.instructionPublic.pdf.pages") }}</span>
    </div>
    <div class="pdfScroll" ref="scroll">
      <div v-if="loading" class="pdfLoading">
        {{ $t("managedInstructionAssignment.instructionPublic.placeholders.documentLoading") }}
      </div>
      <div
        v-for="(ratio, index) in ratios"
        :key="index"
        class="pdfPage"
        :data-page="index + 1"
        :style="{ width: pageWidth + 'px', height: Math.round(pageWidth * ratio) + 'px' }"
      >
        <canvas />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "pdfMobileViewer",
  props: {
    src: { type: String, required: true },
  },
  data: () => ({
    loading: true,
    ratios: [],
    zoom: 1,
    baseWidth: 0,
    minZoom: 1,
    maxZoom: 3,
    step: 0.5,
  }),
  computed: {
    pageWidth() {
      return Math.floor(this.baseWidth * this.zoom);
    },
  },
  watch: {
    src() {
      this.load();
    },
  },
  created() {
    this.doc = null;
    this.stopScroll = null;
    this.visible = new Set();
    this.tasks = {};
    this.renderTimer = null;
    this.scrollFrame = null;
    this.pinch = null;
  },
  mounted() {
    this.measure();
    window.addEventListener("resize", this.onResize);
    const scroll = this.$refs.scroll;
    scroll.addEventListener("touchstart", this.onTouchStart, { passive: true });
    scroll.addEventListener("touchmove", this.onTouchMove, { passive: false });
    scroll.addEventListener("touchend", this.onTouchEnd);
    scroll.addEventListener("touchcancel", this.onTouchEnd);
    scroll.addEventListener("gesturestart", this.preventGesture);
    this.load();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    const scroll = this.$refs.scroll;
    if (scroll) {
      scroll.removeEventListener("touchstart", this.onTouchStart);
      scroll.removeEventListener("touchmove", this.onTouchMove);
      scroll.removeEventListener("touchend", this.onTouchEnd);
      scroll.removeEventListener("touchcancel", this.onTouchEnd);
      scroll.removeEventListener("gesturestart", this.preventGesture);
    }
    clearTimeout(this.renderTimer);
    this.teardown();
  },
  methods: {
    measure() {
      const scroll = this.$refs.scroll;
      if (scroll) this.baseWidth = Math.max(scroll.clientWidth - 16, 200);
    },
    onResize() {
      const before = this.baseWidth;
      this.measure();
      if (before !== this.baseWidth) this.scheduleRender();
    },
    teardown() {
      if (this.stopScroll) this.stopScroll();
      this.stopScroll = null;
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      Object.values(this.tasks).forEach((task) => task.cancel());
      this.tasks = {};
      this.visible = new Set();
      if (this.doc) this.doc.destroy();
      this.doc = null;
    },
    async load() {
      this.teardown();
      this.loading = true;
      this.ratios = [];
      this.zoom = 1;
      const src = this.src;
      try {
        const pdfjs = await import("pdfjs-dist/es5/build/pdf");
        const worker = await import("!!file-loader!pdfjs-dist/es5/build/pdf.worker.min.js");
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default || worker;
        const doc = await pdfjs.getDocument(src).promise;
        if (src !== this.src) {
          doc.destroy();
          return;
        }
        this.doc = doc;
        const ratios = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          const viewport = page.getViewport({ scale: 1 });
          ratios.push(viewport.height / viewport.width);
        }
        this.ratios = ratios;
        this.loading = false;
        await this.$nextTick();
        this.observe();
      } catch (e) {
        if (src === this.src) this.$emit("error", e);
      }
    },
    observe() {
      const root = this.$refs.scroll;
      root.addEventListener("scroll", this.onScroll, { passive: true });
      this.stopScroll = () => root.removeEventListener("scroll", this.onScroll);
      this.updateVisible();
    },
    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateVisible();
      });
    },
    updateVisible() {
      const root = this.$refs.scroll;
      if (!root || !this.doc) return;
      const top = root.scrollTop - 600;
      const bottom = root.scrollTop + root.clientHeight + 600;
      root.querySelectorAll(".pdfPage").forEach((el) => {
        const n = Number(el.dataset.page);
        const inView = el.offsetTop + el.offsetHeight > top && el.offsetTop < bottom;
        if (inView && !this.visible.has(n)) {
          this.visible.add(n);
          this.renderPage(n);
        } else if (!inView && this.visible.has(n)) {
          this.visible.delete(n);
          this.clearPage(n);
        }
      });
    },
    canvasFor(n) {
      const root = this.$refs.scroll;
      return root && root.querySelector(`.pdfPage[data-page="${n}"] canvas`);
    },
    clearPage(n) {
      if (this.tasks[n]) this.tasks[n].cancel();
      delete this.tasks[n];
      const canvas = this.canvasFor(n);
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
    },
    async renderPage(n) {
      if (!this.doc) return;
      const doc = this.doc;
      const page = await doc.getPage(n);
      const canvas = this.canvasFor(n);
      if (!canvas || doc !== this.doc || !this.visible.has(n)) return;
      if (this.tasks[n]) this.tasks[n].cancel();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: (this.pageWidth / base.width) * dpr });
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const task = page.render({ canvasContext: canvas.getContext("2d"), viewport });
      this.tasks[n] = task;
      try {
        await task.promise;
      } catch (e) {
        return;
      }
      if (this.tasks[n] === task) delete this.tasks[n];
    },
    scheduleRender() {
      clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => {
        this.updateVisible();
        this.visible.forEach((n) => this.renderPage(n));
      }, 200);
    },
    clampZoom(value) {
      return Math.min(this.maxZoom, Math.max(this.minZoom, value));
    },
    captureAnchor(x, y) {
      const scroll = this.$refs.scroll;
      const pages = scroll.querySelectorAll(".pdfPage");
      if (!pages.length) return null;
      const rect = scroll.getBoundingClientRect();
      const px = scroll.scrollLeft + x - rect.left;
      const py = scroll.scrollTop + y - rect.top;
      let page = pages[pages.length - 1];
      for (let i = 0; i < pages.length; i++) {
        if (py < pages[i].offsetTop + pages[i].offsetHeight) {
          page = pages[i];
          break;
        }
      }
      return {
        page: page.dataset.page,
        fx: (px - page.offsetLeft) / page.offsetWidth,
        fy: (py - page.offsetTop) / page.offsetHeight,
      };
    },
    applyAnchor(anchor, x, y) {
      if (!anchor) return;
      const scroll = this.$refs.scroll;
      const page = scroll.querySelector(`.pdfPage[data-page="${anchor.page}"]`);
      if (!page) return;
      const rect = scroll.getBoundingClientRect();
      scroll.scrollLeft = page.offsetLeft + anchor.fx * page.offsetWidth - (x - rect.left);
      scroll.scrollTop = page.offsetTop + anchor.fy * page.offsetHeight - (y - rect.top);
    },
    async setZoom(value) {
      const zoom = this.clampZoom(value);
      if (zoom === this.zoom) return;
      const rect = this.$refs.scroll.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      const anchor = this.captureAnchor(x, y);
      this.zoom = zoom;
      await this.$nextTick();
      this.applyAnchor(anchor, x, y);
      this.scheduleRender();
    },
    touchCenter(touches) {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
      };
    },
    touchDistance(touches) {
      return Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
    },
    onTouchStart(e) {
      if (e.touches.length !== 2 || !this.ratios.length) return;
      const center = this.touchCenter(e.touches);
      this.pinch = {
        distance: this.touchDistance(e.touches) || 1,
        zoom: this.zoom,
        anchor: this.captureAnchor(center.x, center.y),
      };
    },
    onTouchMove(e) {
      if (!this.pinch || e.touches.length !== 2) return;
      e.preventDefault();
      const center = this.touchCenter(e.touches);
      const anchor = this.pinch.anchor;
      this.zoom = this.clampZoom((this.pinch.zoom * this.touchDistance(e.touches)) / this.pinch.distance);
      this.$nextTick(() => this.applyAnchor(anchor, center.x, center.y));
    },
    onTouchEnd(e) {
      if (!this.pinch || e.touches.length >= 2) return;
      this.pinch = null;
      this.scheduleRender();
    },
    preventGesture(e) {
      e.preventDefault();
    },
  },
};
</script>

<style scoped>
.pdfViewer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.pdfToolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.zoomBtn,
.zoomValue {
  border: none;
  border-radius: 10px;
  background: #eef2f7;
  color: #111827;
  font-weight: 600;
  cursor: pointer;
}

.zoomBtn {
  width: 36px;
  height: 36px;
  font-size: 20px;
  line-height: 1;
}

.zoomBtn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoomValue {
  min-width: 60px;
  height: 36px;
  font-size: 13px;
}

.pageCount {
  margin-left: auto;
  font-size: 13px;
  color: #6b7280;
}

.pdfScroll {
  position: relative;
  flex: 1;
  overflow: auto;
  touch-action: pan-x pan-y;
  -webkit-overflow-scrolling: touch;
  padding: 8px;
}

.pdfLoading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #666;
}

.pdfPage {
  margin: 0 auto 8px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.pdfPage canvas {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
